import { FormHelperText } from '@mui/material'
import React, { useRef } from 'react'
import { MouseEventHandler, useCallback, useEffect, useState } from 'react'
import { ErrorCode as DropZoneErrorCode, useDropzone } from 'react-dropzone'
import { useController, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useTypedDispatch } from '../../'
import {
  mimeFromExtension,
  postDigitalAssetAction,
  removeSelectedDigitalAsset,
} from '../../actions/create-defect'
import { DEFECT_ACCEPTED_FILE_TYPES } from '../../constants'
import { removeFile } from '../../reducers/createDefectSlice'
import { ActionStatus } from '../../types/Input'
import ConfirmationDialog from './ConfirmationDialog'
import { FormErrorHelperText } from './FormErrorHelperText'
import {
  AddAnotherFileText,
  FileErrors,
  FileList,
  UploadBox,
  UploadInstructions,
} from './input/AttachmentUpload'
import { AssetIdentifiers } from './types'

interface AttachmentUploadProps {
  instructions: string
  fileWord: string
  onChange?: (files?: string[]) => void
  files: AssetIdentifiers
  status?: ActionStatus
  required?: boolean
  label?: string
  name: string
}

type PostDigitalAssetUpload = ReturnType<ReturnType<typeof postDigitalAssetAction>>

const MB = 2 ** 20
// FC API recompresses images and videos, so they are limited only by its
// DigitalAssetS3MaxBytes configuration (1 GB). The other accepted types are forwarded
// onward unconverted as base64 and are hard-capped at ~36.5 MB in FC API
// (DigitalAssetS3Handler.MAX_NO_CONVERSION_BYTES), so stay just under that.
export const MAX_FILE_SIZE = 35 * MB
export const MAX_CONVERTED_FILE_SIZE = 950 * MB

const maxSizeForFile = (file: File): number => {
  const mime = file.type || mimeFromExtension(file.name) || ''
  return mime.startsWith('image/') || mime.startsWith('video/')
    ? MAX_CONVERTED_FILE_SIZE
    : MAX_FILE_SIZE
}

export const AttachmentUpload = ({
  instructions,
  fileWord,
  required = false,
  onChange: customOnChange,
  status,
  files,
  label,
  name,
}: AttachmentUploadProps): JSX.Element => {
  const { control } = useFormContext()
  const { t } = useTranslation()
  const dispatch = useTypedDispatch()
  const [open, setOpen] = useState(false)
  const [fileToDelete, setFileToDelete] = useState('')
  const id = `${name}-${label}`
  const pendingUploadsRef = useRef<PostDigitalAssetUpload[]>([])

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: { required: { value: required, message: t('field.requiredHelperText') } },
  })

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((acceptedFile) => {
        const upload = dispatch(postDigitalAssetAction(acceptedFile))
        pendingUploadsRef.current.push(upload)
        upload.finally(() => {
          pendingUploadsRef.current = pendingUploadsRef.current.filter((item) => item !== upload)
        })
      })
    },
    [dispatch]
  )

  useEffect(() => {
    return () => {
      pendingUploadsRef.current.forEach((upload) => upload.abort())
      pendingUploadsRef.current = []
    }
  }, [])

  useEffect(() => {
    if (customOnChange) {
      customOnChange(Object.keys(files))
    }
  }, [customOnChange, files])

  const dropZoneState = useDropzone({
    onDrop,
    accept: DEFECT_ACCEPTED_FILE_TYPES,
    validator: (file) =>
      file.size > maxSizeForFile(file)
        ? { code: DropZoneErrorCode.FileTooLarge, message: 'File is larger than allowed' }
        : null,
  })

  const handleClickOpen = (): void => {
    setOpen(true)
  }

  const handleClose = (): void => {
    setOpen(false)
  }

  const remove = (id: string): void => {
    removeSelectedDigitalAsset(id)
    dispatch(removeFile(id))
    setFileToDelete('')
  }

  const deleteAttachment =
    (id: string): MouseEventHandler<HTMLButtonElement> =>
    (e): void => {
      e.stopPropagation()
      setFileToDelete(id)
      handleClickOpen()
    }

  const fileErrors = dropZoneState.fileRejections.map((fileRejection) => {
    const {
      file: { name: fileName },
      errors: fileErrors,
    } = fileRejection
    const errorMessages = fileErrors.map((fileError) => {
      const { message, code } = fileError
      switch (code) {
        case DropZoneErrorCode.FileTooLarge:
          return t('defect.attachmentTooBig', {
            maxSize: Math.floor(maxSizeForFile(fileRejection.file) / MB),
          })
        case DropZoneErrorCode.FileInvalidType:
          return t('defect.invalidFileType', {
            accept: Object.values(DEFECT_ACCEPTED_FILE_TYPES).flat().join(' '),
          })
        default:
          return message
      }
    })
    return { fileName, messages: errorMessages }
  })

  return (
    <>
      <input type="hidden" value={field.value} />
      <UploadBox
        onBlur={field.onBlur}
        error={!!error?.message}
        id={id}
        dropzoneProps={dropZoneState}
      >
        {Object.keys(files).length > 0 ? (
          <>
            <FileList files={files} status={status} deleteAttachment={deleteAttachment} />
            <AddAnotherFileText>{t('defect.addAnotherFile')}</AddAnotherFileText>
          </>
        ) : (
          <UploadInstructions
            instructions={instructions}
            fileWord={fileWord}
            t={t}
            required={required}
          />
        )}
      </UploadBox>
      {error?.message && (
        <FormHelperText component="div">
          <FormErrorHelperText error={error.message} />
        </FormHelperText>
      )}
      {fileErrors?.length > 0 && <FileErrors errorsList={fileErrors} />}
      {open && (
        <ConfirmationDialog
          closeDialog={handleClose}
          submitHandler={() => {
            remove(fileToDelete)
            handleClose()
          }}
          dialogText={t('defect.removeAttachmentConfirmation')}
          primaryActionText={t('action.confirmRemove')}
        />
      )}
    </>
  )
}
